/** AskGuru-only query normalization - does not affect global nav search. */

const STOP_WORDS = new Set([
  'what',
  'does',
  'do',
  'mean',
  'means',
  'this',
  'that',
  'these',
  'those',
  'please',
  'explain',
  'tell',
  'about',
  'how',
  'why',
  'when',
  'where',
  'can',
  'could',
  'would',
  'should',
  'the',
  'a',
  'an',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'me',
  'my',
  'i',
  'you',
  'your',
  'we',
  'our',
  'they',
  'their',
  'it',
  'its',
  'of',
  'to',
  'for',
  'with',
  'on',
  'at',
  'by',
  'from',
  'as',
  'if',
  'or',
  'and',
  'but',
  'so',
  'just',
  'also',
  'really',
  'very',
  'kya',
  'hai',
  'hota',
  'hoti',
  'ka',
  'ki',
  'ke',
  'ko',
  'se',
  'mein',
  'kaise',
  'kyun',
  'kya',
  'matlab',
  'arth',
  'batao',
  'bataiye',
  'samjhao',
  'samjhaye',
]);

/** Word ordinals → numeric ordinal suffix (1st, 2nd, …). */
const WORD_ORDINALS = {
  first: '1st',
  second: '2nd',
  third: '3rd',
  fourth: '4th',
  fifth: '5th',
  sixth: '6th',
  seventh: '7th',
  eighth: '8th',
  ninth: '9th',
  tenth: '10th',
  eleventh: '11th',
  twelfth: '12th',
  one: '1st',
  two: '2nd',
  three: '3rd',
  four: '4th',
  five: '5th',
  six: '6th',
  seven: '7th',
  eight: '8th',
  nine: '9th',
  ten: '10th',
  eleven: '11th',
};

const JYOTISHA_SIGNALS =
  /\b(house|houses|lord|lords|bhava|bhav|bhavesh|lagnesh|graha|grahas|lagna|ascendant|dasha|dashas|mahadasha|gochar|transit|rashi|rashis|nakshatra|nakshatras|kundali|kundli|navamsa|varga|yoga|yogas|jyotish|jyotisha|astrology|horoscope|chart|planets|saturn|jupiter|mars|venus|mercury|moon|sun|rahu|ketu|maraka|trikona|kendra|dusthana|phal|reading|भाव|स्वामी|भावेश|लग्न|ग्रह|राशि|दशा|गोचर|नक्षत्र|कुंडली|योग|ज्योतिष)\b/i;

const HOUSE_LORD_PATTERN =
  /(?:(\d{1,2})(?:st|nd|rd|th)?|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*(?:lord|lords|house\s*lord|bhavesh|bhava\s*lord|swami|adhipati)\s+(?:(?:in|into|placed\s+in|sitting\s+in|goes\s+to)\s+)?(?:the\s+)?(?:(\d{1,2})(?:st|nd|rd|th)?|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s*(?:house|bhava|bhav)/i;

/** Topics boosted when house-lord placement intent is detected. */
export const HOUSE_LORD_BOOST_IDS = [
  'bhava-lords-in-houses',
  'calc-house-lord-placement',
];

function fold(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * @param {string} word
 * @returns {string}
 */
function normalizeOrdinalWord(word) {
  const w = word.toLowerCase().replace(/[.,!?;:'"]/g, '');
  if (WORD_ORDINALS[w]) return WORD_ORDINALS[w];
  const numMatch = w.match(/^(\d{1,2})(?:st|nd|rd|th)?$/);
  if (numMatch) {
    const n = parseInt(numMatch[1], 10);
    if (n >= 1 && n <= 12) {
      const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
      return `${n}${suffix}`;
    }
  }
  return w;
}

/**
 * @param {string} query
 * @returns {{
 *   normalizedQuery: string;
 *   contentTokens: string[];
 *   intent: 'house_lord_placement' | null;
 *   boostTopicIds: string[];
 *   isJyotishaSignal: boolean;
 * }}
 */
export function normalizeAskGuruQuery(query) {
  const raw = String(query || '').trim();
  const folded = fold(raw);

  const preprocessed = folded
    .replace(/(\d+)\s*(st|nd|rd|th)\b/gi, '$1$2')
    .replace(/["'`]/g, ' ');

  const tokens = preprocessed.split(/\s+/).filter(Boolean);
  const normalizedTokens = tokens.map(normalizeOrdinalWord);
  const contentTokens = normalizedTokens.filter((t) => t.length >= 2 && !STOP_WORDS.has(t));

  const normalizedQuery = contentTokens.join(' ') || normalizedTokens.join(' ') || raw;

  let forPattern = normalizedQuery;
  for (const [word, ord] of Object.entries(WORD_ORDINALS)) {
    forPattern = forPattern.replace(new RegExp(`\\b${word}\\b`, 'g'), ord);
  }

  const houseLordMatch =
    HOUSE_LORD_PATTERN.test(forPattern) ||
    HOUSE_LORD_PATTERN.test(folded.replace(/\b(eight|fifth|first|second|third|fourth|sixth|seventh|ninth|tenth|eleventh|twelfth)\b/g, (m) => WORD_ORDINALS[m] || m));
  const intent = houseLordMatch ? 'house_lord_placement' : null;

  const isJyotishaSignal =
    Boolean(intent) ||
    JYOTISHA_SIGNALS.test(folded) ||
    JYOTISHA_SIGNALS.test(normalizedQuery) ||
    contentTokens.some((t) => ['lord', 'house', 'bhava', 'bhavesh', 'lagnesh', 'graha', 'lagna'].includes(t));

  return {
    normalizedQuery,
    contentTokens,
    intent,
    boostTopicIds: intent ? [...HOUSE_LORD_BOOST_IDS] : [],
    isJyotishaSignal,
  };
}
